import { useMemo } from 'react';
import { useBannerAutoUpgradeNotATrial } from '@atlassian/jira-auto-upgrade-not-a-trial/src/controllers/banner/index.tsx';
import { useBannerCookieConsent } from '@atlassian/jira-cookie-consent-banner/src/controllers/index.tsx';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useBannerIPBlocklisting } from '@atlassian/jira-ip-block-listing-banner/src/controllers/index.tsx';
import { useIsChromeless } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useBannerAnnouncement } from '@atlassian/jira-platform-ui-banners-announcement/src/controllers/announcement/index.tsx';
import { useCohort3CustomersBanner } from '@atlassian/jira-platform-ui-banners-eoc-cohort3-customers-banner/src/controllers/index.tsx';
import { useBannerEocTransition } from '@atlassian/jira-platform-ui-banners-eoc-transition/src/controllers/index.tsx';
import { useBannerMailExpiry } from '@atlassian/jira-platform-ui-mail-expiry-reminder/src/controllers/use-banner-mail-expiry/index.tsx';
import { useBannerJpdBlueprintsFeedback } from '@atlassian/jira-polaris-component-blueprints-feedback-banner/src/controllers/index.tsx';
import { useBannerReturnToMobile } from '@atlassian/jira-return-to-mobile-banner/src/controllers/index.tsx';
import { useBannerAutomationLimitAwareness } from '@atlassian/jira-standard-automation-limit-awareness-modal/src/controllers/banner/index.tsx';

type ReturnType = {
	name: string | null;
	height: number;
	isEnabled: boolean;
};

const noBanner: ReturnType = {
	name: null,
	height: 0,
	isEnabled: false,
};

type Props = {
	ignoreChromeless?: boolean;
};

const useJpdBannerFeedback = functionWithCondition(
	() => fg('jpd-view-templates-sample-project'),

	() => useBannerJpdBlueprintsFeedback(),
	() => noBanner,
);

const useAutoUpgradeBanner = functionWithCondition(
	() => fg('auto_upgrade_not_a_trial_gate'),

	() => useBannerAutoUpgradeNotATrial(),
	() => noBanner,
);

export const useBanner = ({ ignoreChromeless = false }: Props = {}): ReturnType => {
	const [isChromeless] = useIsChromeless();
	const announcement = useBannerAnnouncement();

	const transition = useBannerEocTransition();
	const returnToMobile = useBannerReturnToMobile();
	const IPBlocklisting = useBannerIPBlocklisting();

	// eslint-disable-next-line react-hooks/react-compiler
	const cohort3CustomersBanner = fg('eoc_consolidation_cohort_3_banner') // eslint-disable-next-line react-hooks/rules-of-hooks
		? useCohort3CustomersBanner()
		: undefined;

	const cookieConsent = useBannerCookieConsent();

	// we have added this check to return banner data for assets routes as all the routes in assets are chromeless
	// but we want to show the announcement banner in assets routes
	const isChromelessCheck =
		ignoreChromeless && fg('show_announcement_banner_in_assets') ? false : isChromeless;
	const mailExpiry = useBannerMailExpiry();

	const jpdBlueprintsFeedback = useJpdBannerFeedback();

	// eslint-disable-next-line react-hooks/react-compiler
	const automationLimitAwareness = fg('upsell_jira_global_automations_limit_banner_fg') // eslint-disable-next-line react-hooks/rules-of-hooks
		? useBannerAutomationLimitAwareness()
		: undefined;

	const autoUpgradeNotATrial = useAutoUpgradeBanner();

	return useMemo(() => {
		// cookie consent banner has the highest priority
		if (cookieConsent?.isEnabled) return cookieConsent;
		if (isChromelessCheck) return noBanner;
		if (IPBlocklisting.isEnabled) return IPBlocklisting;
		if (returnToMobile.isEnabled) return returnToMobile;
		if (announcement.isEnabled) return announcement;
		if (transition?.isEnabled) return transition;
		if (mailExpiry?.isEnabled) return mailExpiry;
		if (jpdBlueprintsFeedback.isEnabled) return jpdBlueprintsFeedback;
		if (automationLimitAwareness?.isEnabled) return automationLimitAwareness;
		if (cohort3CustomersBanner?.isEnabled) return cohort3CustomersBanner;
		if (autoUpgradeNotATrial?.isEnabled) return autoUpgradeNotATrial;
		return noBanner;
	}, [
		cookieConsent,
		IPBlocklisting,
		returnToMobile,
		announcement,
		transition,
		mailExpiry,
		jpdBlueprintsFeedback,
		automationLimitAwareness,
		cohort3CustomersBanner,
		isChromelessCheck,
		autoUpgradeNotATrial,
	]);
};
